import { useState } from 'react';
import { ArrowLeft, UserPlus, Loader2, AlertCircle } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

interface JoinWorkspaceViewProps {
    onNavigate: (view: string) => void;
}

export default function JoinWorkspaceView({ onNavigate }: JoinWorkspaceViewProps) {
    const { joinWorkspace } = useWorkspace();
    const [inviteCode, setInviteCode] = useState('');
    const [isJoining, setIsJoining] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleJoin = async () => {
        if (!inviteCode.trim()) {
            setError('Please enter an invite code');
            return;
        }

        setIsJoining(true);
        setError(null);

        const result = await joinWorkspace(inviteCode.trim());

        setIsJoining(false);

        if (result) {
            onNavigate('dashboard');
        } else {
            setError('Could not join workspace. The invitation system is coming soon.');
        }
    };

    return (
        <div className="max-w-md mx-auto py-8 animate-fade-in">
            {/* Header */}
            <div className="flex items-center gap-3 mb-8">
                <button
                    onClick={() => onNavigate('dashboard')}
                    className="p-2 hover:bg-hig-bg-tertiary rounded-lg transition-colors text-hig-text-secondary"
                >
                    <ArrowLeft size={20} />
                </button>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <UserPlus size={20} className="text-white" />
                </div>
                <div>
                    <h1 className="text-xl font-bold text-hig-text-primary">Join Workspace</h1>
                    <p className="text-sm text-hig-text-secondary">Enter an invite code to join an existing workspace</p>
                </div>
            </div>

            <div className="card p-6 space-y-6">
                {/* Invite Code Input */}
                <div>
                    <label className="block text-sm font-semibold text-hig-text-primary mb-2">
                        Invite Code
                    </label>
                    <input
                        type="text"
                        value={inviteCode}
                        onChange={e => setInviteCode(e.target.value)}
                        placeholder="Paste your invite code here"
                        className="w-full bg-hig-bg-tertiary border border-hig-border rounded-lg px-4 py-3 text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:ring-2 focus:ring-hig-accent/40 font-mono"
                        autoFocus
                        onKeyDown={e => e.key === 'Enter' && handleJoin()}
                    />
                    <p className="text-xs text-hig-text-tertiary mt-2">
                        Ask your workspace owner for an invite code or check your email for an invitation link.
                    </p>
                </div>

                {/* Info Banner */}
                <div className="flex items-start gap-3 p-4 bg-hig-accent/10 border border-hig-accent/20 rounded-lg">
                    <AlertCircle size={16} className="text-hig-accent flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-hig-accent">
                        <p className="font-medium mb-1">What happens when you join?</p>
                        <ul className="space-y-1 text-hig-accent/80">
                            <li>• You'll be added as a <strong>viewer</strong> by default</li>
                            <li>• The workspace owner can upgrade your role</li>
                            <li>• You can leave the workspace at any time</li>
                        </ul>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <p className="text-sm text-red-400">{error}</p>
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                    <button
                        onClick={() => onNavigate('dashboard')}
                        className="flex-1 py-3 bg-hig-bg-tertiary hover:bg-hig-bg-elevated text-hig-text-primary rounded-xl text-sm font-medium transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleJoin}
                        disabled={isJoining || !inviteCode.trim()}
                        className="flex-1 py-3 bg-hig-accent hover:bg-hig-accent/90 text-white rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                        {isJoining ? (
                            <>
                                <Loader2 size={16} className="animate-spin" />
                                Joining…
                            </>
                        ) : (
                            <>
                                <UserPlus size={16} />
                                Join Workspace
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
